import React, { FC } from 'react';
import { useTranslation } from 'next-i18next';
import type { OptimizedBoatSearchDetail } from 'src/common/types/api/v4/boats/search';
import type { DlpPageStatsProps } from 'src/search/components/DlpPageStats';
import type { Title } from 'src/common/types/api/v4/search/v1/landing';
import type { CategorySlug } from 'src/common/boatCategories';
import { PATHS, SHARED_MEDIA_QUERIES as sharedMediaQueries } from 'src/common/constants';
import { DEFAULT_SLUG } from 'src/search/constants';
import DlpPageStats from 'src/search/components/DlpPageStats';
import { CarouselProvider } from 'src/common/providers/CarouselProvider';
import Carousel from 'src/common/components/dockyard/Carousel';
import Typography from 'src/common/components/Typography';
import ListingCard, { ListingCardGtagEvent, ListingCardVariants } from 'src/common/components/ListingCard';
import Button from 'src/common/components/ButtonDS22';
import useMobileNativeWebView from 'src/common/hooks/useMobileNativeWebView';
import GulperImpressions from 'src/common/components/GulperImpressions';
import { useSearchInputContext } from 'src/common/components/Search/SearchInputProvider';
import { formatDate } from 'src/common/utils/dateHelpers';
import styles from './DlpListings.module.scss';

export type DlpListingsProps = {
  listings?: OptimizedBoatSearchDetail[];
  title?: Title;
  subTitle?: React.ReactNode;
  categorySlug?: CategorySlug | typeof DEFAULT_SLUG | null;
  locationSlug?: string;
  pageStats?: DlpPageStatsProps;
  /**
   * Is this component displayed in the First Contentful Paint?
   * If true, it should eagerly load images, else slower FCP.
   *
  * Lighthouse: "Above-the-fold images that are lazily loaded render later in the page lifecycle,
  *  which can delay the largest contentful paint."
  */
  firstContentfulPaint?: boolean;
  listingCardVariant?: ListingCardVariants;
  gtagEvent?: ListingCardGtagEvent;
  isInstabookCarousel?: boolean;
  'data-gulper-section': 'Search' | 'BookNow' | 'MostPopular' | 'Broadcast';
};

const DlpListings: FC<DlpListingsProps> = ({
  listings,
  title,
  subTitle,
  categorySlug,
  pageStats,
  locationSlug,
  firstContentfulPaint,
  listingCardVariant,
  gtagEvent,
  isInstabookCarousel = false,
  ...dataAttributes
}) => {
  const [t] = useTranslation('discovery-landing-pages');
  const { isMobileNativeWebView } = useMobileNativeWebView();
  const { filterDates } = useSearchInputContext();
  const searchDates = filterDates?.map((date) => formatDate(date));

  const buttonHref = () => {
    const categorySegment = categorySlug === DEFAULT_SLUG ? '' : `${categorySlug}/`;
    const locationSegment = locationSlug ? `${locationSlug}/` : '';
    const basePath = `${PATHS.SEARCH}${categorySegment}${locationSegment}`;
    const params = new URLSearchParams({ page: '1' });
    if (isInstabookCarousel) {
      params.set('instabooks', 'true');
    }
    if (searchDates?.length) {
      params.set('dates', searchDates.join(','));
    }
    return `${basePath}?${params.toString()}`;
  };

  return (
    <div className={styles.root} {...dataAttributes}>
      <div className={styles.heading}>
        <Typography
          type="h3"
          size="heading-3"
          weight="semibold"
        >
          {title}
        </Typography>
        {subTitle}
        {pageStats && (
          <div className={styles.pageStats}>
            <DlpPageStats
              breadcrumbs={pageStats.breadcrumbs}
              average={pageStats.average}
              total={pageStats.total}
            />
          </div>
        )}
      </div>
      <CarouselProvider
        emblaOptions={{
          active: true,
          breakpoints: {
            [sharedMediaQueries.m]: {
              active: false,
            },
          },
        }}
      >
        <Carousel>
          {listings?.map((listing, index) => {
            if (!listing.instabook_enabled && isInstabookCarousel) return null;
            return (
              <ListingCard
                key={listing.id}
                listingId={listing.id}
                listingUrl={listing.listing_url}
                headline={listing.headline}
                breadcrumbs={listing.breadcrumbs}
                category={listing.cat_loc_breadcrumb}
                rating={listing.review_average ? parseFloat(listing.review_average) : undefined}
                awards={listing.user_details.awards}
                isSuperOwner={listing.user_details.is_super_owner}
                instantBookingAvailable={listing.has_available_instabook}
                searchPricing={listing.search_price_nested}
                imageSrc={listing.photo_url ?? ''}
                capacity={t('listings-capacity', { capacity: listing.capacity })}
                snoozeStartDate={listing?.snooze_start_date}
                snoozeEndDate={listing?.snooze_end_date}
                loading={firstContentfulPaint && index === 0 ? 'eager' : 'lazy'}
                withFavorite={!isMobileNativeWebView}
                variant={listingCardVariant}
                gtagEvent={gtagEvent}
              />
            );
          })}
        </Carousel>
        <GulperImpressions data={listings} />
      </CarouselProvider>
      <Button
        type="link"
        href={buttonHref()}
        variant="tertiary"
        fitWidth="m"
      >
        {t('listings--cta')}
      </Button>
    </div>
  );
};

export default DlpListings;
